import { useEffect, useState } from 'react'
import { api } from '../api'
import {
  Card, Button, Form, Input, InputNumber, Select, DatePicker, Tag, Empty, Spin, Space, Typography,
  Row, Col, Drawer, Statistic, message, Popconfirm, Tabs, Switch, Checkbox, Divider, List, Modal
} from 'antd'
import {
  PlusOutlined, DeleteOutlined, EditOutlined, CalendarOutlined, UserOutlined, CheckOutlined,
  EuroOutlined, EnvironmentOutlined, PhoneOutlined, MailOutlined, DashboardOutlined,
  TeamOutlined, ToolOutlined, ClockCircleOutlined, ShopOutlined, WarningOutlined, DollarOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography

const tipoFactColor = { hora: 'purple', servicio: 'blue', tahulla: 'green' }
const tipoFactLabel = { hora: 'Por hora', servicio: 'Por servicio', tahulla: 'Por tahulla' }
const tipoFactUnidad = { hora: 'h', servicio: 'serv', tahulla: 'tah' }

export default function Servicios() {
  const isMobile = useIsMobile()
  const [tab, setTab] = useState('servicios')
  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px' }}>
        <Title level={4} style={{ color: 'white', margin: 0 }}>Servicios Tractor</Title>
      </div>
      <Tabs
        activeKey={tab}
        onChange={setTab}
        centered
        style={{ background: 'white', paddingTop: '4px' }}
        items={[
          { key: 'servicios', label: <span><CalendarOutlined /> Servicios</span> },
          { key: 'dashboard', label: <span><DashboardOutlined /> Dashboard</span> },
          { key: 'clientes', label: <span><TeamOutlined /> Clientes</span> },
          { key: 'tipos', label: <span><ToolOutlined /> Tipos trabajo</span> },
        ]}
      />
      <div style={{ padding: '16px' }}>
        {tab === 'servicios' && <VistaServicios />}
        {tab === 'dashboard' && <VistaDashboard />}
        {tab === 'clientes' && <VistaClientes />}
        {tab === 'tipos' && <VistaTipos />}
      </div>
    </div>
  )
}

// ==================== SERVICIOS ====================
function VistaServicios() {
  const isMobile = useIsMobile()
  const [servicios, setServicios] = useState([])
  const [clientes, setClientes] = useState([])
  const [tipos, setTipos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [filtroCliente, setFiltroCliente] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setCargando(true)
    try {
      const [s, c, t] = await Promise.all([api('/servicios'), api('/clientes'), api('/tipos-trabajo')])
      setServicios(s); setClientes(c); setTipos(t)
    } catch (err) { message.error('Error cargando datos') }
    setCargando(false)
  }

  const abrirNuevo = () => {
    setEditando(null)
    form.resetFields()
    form.setFieldsValue({ fecha: dayjs(), cantidad: 1, cobrado: false })
    setMostrarForm(true)
  }

  const abrirEditar = (s) => {
    setEditando(s)
    form.setFieldsValue({
      cliente_id: s.cliente_id,
      tipo_trabajo_id: s.tipo_trabajo_id,
      fecha: dayjs(s.fecha),
      cantidad: parseFloat(s.cantidad),
      precio_aplicado: parseFloat(s.precio_aplicado),
      cobrado: s.cobrado,
      fecha_cobro: s.fecha_cobro ? dayjs(s.fecha_cobro) : null,
      metodo_cobro: s.metodo_cobro,
      ubicacion: s.ubicacion,
      parcela_ref: s.parcela_ref,
      latitud: s.latitud ? parseFloat(s.latitud) : null,
      longitud: s.longitud ? parseFloat(s.longitud) : null,
      notas: s.notas,
    })
    setMostrarForm(true)
  }

  const autoRellenarPrecio = async () => {
    const cliente_id = form.getFieldValue('cliente_id')
    const tipo_trabajo_id = form.getFieldValue('tipo_trabajo_id')
    if (!cliente_id || !tipo_trabajo_id) return
    try {
      const r = await api(`/servicios/precio-efectivo?cliente_id=${cliente_id}&tipo_trabajo_id=${tipo_trabajo_id}`)
      form.setFieldsValue({ precio_aplicado: r.precio })
      if (r.origen === 'cliente') message.info('Precio específico del cliente aplicado')
    } catch (err) { /* silencioso */ }
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      const payload = {
        cliente_id: values.cliente_id,
        tipo_trabajo_id: values.tipo_trabajo_id,
        fecha: values.fecha.format('YYYY-MM-DD'),
        cantidad: values.cantidad,
        precio_aplicado: values.precio_aplicado,
        cobrado: values.cobrado === true,
        fecha_cobro: values.fecha_cobro ? values.fecha_cobro.format('YYYY-MM-DD') : null,
        metodo_cobro: values.metodo_cobro || null,
        ubicacion: values.ubicacion || null,
        latitud: values.latitud || null,
        longitud: values.longitud || null,
        parcela_ref: values.parcela_ref || null,
        notas: values.notas || null,
      }
      if (editando) {
        await api(`/servicios/${editando.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        message.success('Servicio actualizado')
      } else {
        await api('/servicios', { method: 'POST', body: JSON.stringify(payload) })
        message.success('Servicio registrado')
      }
      setMostrarForm(false)
      cargar()
    } catch (err) { message.error(err.message || 'Error guardando servicio') }
    setGuardando(false)
  }

  const toggleCobrado = async (s) => {
    try {
      await api(`/servicios/${s.id}/cobrar`, {
        method: 'PATCH',
        body: JSON.stringify({ cobrado: !s.cobrado, fecha_cobro: dayjs().format('YYYY-MM-DD') }),
      })
      cargar()
    } catch (err) { message.error('Error actualizando cobro') }
  }

  const eliminar = async (id) => {
    try {
      await api(`/servicios/${id}`, { method: 'DELETE' })
      message.success('Eliminado')
      cargar()
    } catch (err) { message.error('Error eliminando') }
  }

  const cantidad = Form.useWatch('cantidad', form)
  const precio = Form.useWatch('precio_aplicado', form)
  const cobrado = Form.useWatch('cobrado', form)
  const importe = (parseFloat(cantidad) || 0) * (parseFloat(precio) || 0)

  const tipoSel = tipos.find(t => t.id === form.getFieldValue('tipo_trabajo_id'))

  // Filtrar
  let serviciosFiltrados = servicios
  if (filtroEstado === 'pendiente') serviciosFiltrados = serviciosFiltrados.filter(s => !s.cobrado)
  if (filtroEstado === 'cobrado') serviciosFiltrados = serviciosFiltrados.filter(s => s.cobrado)
  if (filtroCliente) serviciosFiltrados = serviciosFiltrados.filter(s => s.cliente_id === filtroCliente)

  // Stats rápidos
  const mesActual = dayjs().format('YYYY-MM')
  const facturadoMes = servicios.filter(s => s.fecha && s.fecha.startsWith(mesActual)).reduce((a, s) => a + parseFloat(s.importe_total || 0), 0)
  const pendienteTotal = servicios.filter(s => !s.cobrado).reduce((a, s) => a + parseFloat(s.importe_total || 0), 0)

  return (
    <>
      <Row gutter={8} style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Card size="small" style={{ borderRadius: 10, textAlign: 'center' }}>
            <Statistic title={<Text style={{ fontSize: 11 }}>Facturado este mes</Text>} value={facturadoMes} precision={2} suffix="€" valueStyle={{ fontSize: 20, color: '#2E7D32' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" style={{ borderRadius: 10, textAlign: 'center' }}>
            <Statistic title={<Text style={{ fontSize: 11 }}>Pendiente cobro</Text>} value={pendienteTotal} precision={2} suffix="€" valueStyle={{ fontSize: 20, color: '#E65100' }} />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <Select value={filtroEstado} onChange={setFiltroEstado} size="small" style={{ width: 130 }}>
            <Select.Option value="todos">Todos</Select.Option>
            <Select.Option value="pendiente">Pendientes</Select.Option>
            <Select.Option value="cobrado">Cobrados</Select.Option>
          </Select>
          <Select placeholder="Cliente" value={filtroCliente} onChange={setFiltroCliente} allowClear size="small" style={{ width: 160 }} showSearch optionFilterProp="children">
            {clientes.map(c => <Select.Option key={c.id} value={c.id}>{c.nombre}</Select.Option>)}
          </Select>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32' }}>Nuevo</Button>
      </Space>

      <Drawer
        title={editando ? 'Editar servicio' : 'Nuevo servicio'}
        open={mostrarForm}
        onClose={() => setMostrarForm(false)}
        width={isMobile ? '100%' : 420}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setMostrarForm(false)}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>Guardar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Cliente" name="cliente_id" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Select placeholder="Selecciona cliente" onChange={autoRellenarPrecio} showSearch optionFilterProp="children">
              {clientes.filter(c => c.activo !== false).map(c => (
                <Select.Option key={c.id} value={c.id}>{c.nombre}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Tipo de trabajo" name="tipo_trabajo_id" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Select placeholder="Selecciona tipo" onChange={autoRellenarPrecio} showSearch optionFilterProp="children">
              {tipos.filter(t => t.activo !== false).map(t => (
                <Select.Option key={t.id} value={t.id}>
                  {t.nombre} <Tag color={tipoFactColor[t.tipo_facturacion]} style={{ marginLeft: 4, fontSize: 10 }}>{tipoFactLabel[t.tipo_facturacion]}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Fecha" name="fecha" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label={`Cantidad (${tipoSel ? tipoFactUnidad[tipoSel.tipo_facturacion] : ''})`} name="cantidad" rules={[{ required: true }]}>
                <InputNumber min={0.01} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Precio unit. (€)" name="precio_aplicado" rules={[{ required: true }]}>
                <InputNumber min={0} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 10, marginBottom: 16, textAlign: 'center' }}>
            <Text strong style={{ fontSize: 18, color: '#389e0d' }}>Importe total: {importe.toFixed(2)} €</Text>
          </div>
          <Divider style={{ margin: '8px 0' }}>Ubicación</Divider>
          <Form.Item label="Ubicación / dirección de la parcela" name="ubicacion">
            <Input placeholder="Ej: Camino de la Granja, Cox" />
          </Form.Item>
          <Form.Item label="Referencia parcela" name="parcela_ref">
            <Input placeholder="Polígono / Parcela / Ref catastral" />
          </Form.Item>
          <Row gutter={8}>
            <Col span={12}><Form.Item label="Latitud" name="latitud"><InputNumber step={0.000001} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item label="Longitud" name="longitud"><InputNumber step={0.000001} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Divider style={{ margin: '8px 0' }}>Cobro</Divider>
          <Form.Item name="cobrado" valuePropName="checked">
            <Checkbox>Cobrado</Checkbox>
          </Form.Item>
          {cobrado && (
            <>
              <Form.Item label="Fecha de cobro" name="fecha_cobro">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item label="Método de cobro" name="metodo_cobro">
                <Select allowClear placeholder="Método">
                  <Select.Option value="efectivo">Efectivo</Select.Option>
                  <Select.Option value="transferencia">Transferencia</Select.Option>
                  <Select.Option value="bizum">Bizum</Select.Option>
                  <Select.Option value="tarjeta">Tarjeta</Select.Option>
                  <Select.Option value="otro">Otro</Select.Option>
                </Select>
              </Form.Item>
            </>
          )}
          <Form.Item label="Notas" name="notas">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>

      {cargando ? (
        <div style={{ textAlign: 'center', marginTop: 60 }}><Spin size="large" /></div>
      ) : serviciosFiltrados.length === 0 ? (
        <Empty description="No hay servicios" style={{ marginTop: 40 }} />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {serviciosFiltrados.map(s => {
            const dias = s.cobrado ? null : dayjs().diff(dayjs(s.fecha), 'day')
            return (
              <Card
                key={s.id}
                size="small"
                hoverable
                style={{ borderRadius: 12, borderLeft: `3px solid ${s.cobrado ? '#2E7D32' : '#E65100'}` }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Text strong style={{ fontSize: 14 }}>{s.cliente_nombre}</Text>
                      {s.cobrado
                        ? <Tag color="green" style={{ fontSize: 10 }}><CheckOutlined /> Cobrado</Tag>
                        : <Tag color={dias > 30 ? 'red' : dias > 15 ? 'orange' : 'gold'} style={{ fontSize: 10 }}>Pendiente {dias}d</Tag>}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                      <CalendarOutlined /> {dayjs(s.fecha).format('DD/MM/YYYY')}
                      <Tag color={tipoFactColor[s.tipo_facturacion]} style={{ marginLeft: 6, fontSize: 10 }}>{s.tipo_trabajo_nombre}</Tag>
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13 }}>
                      <Text>{parseFloat(s.cantidad)} × {parseFloat(s.precio_aplicado).toFixed(2)} € = </Text>
                      <Text strong style={{ color: '#1565C0', fontSize: 15 }}>{parseFloat(s.importe_total).toFixed(2)} €</Text>
                    </div>
                    {s.ubicacion && (
                      <div style={{ marginTop: 2, fontSize: 11, color: '#666' }}>
                        <EnvironmentOutlined /> {s.ubicacion}
                      </div>
                    )}
                    {s.fecha_cobro && (
                      <div style={{ marginTop: 2, fontSize: 11, color: '#2E7D32' }}>
                        Cobrado el {dayjs(s.fecha_cobro).format('DD/MM/YYYY')} {s.metodo_cobro ? `· ${s.metodo_cobro}` : ''}
                      </div>
                    )}
                    {s.notas && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>{s.notas}</Text>}
                  </div>
                  <Space direction="vertical" size={2}>
                    <Button
                      type={s.cobrado ? 'default' : 'primary'}
                      size="small"
                      icon={s.cobrado ? <WarningOutlined /> : <CheckOutlined />}
                      onClick={() => toggleCobrado(s)}
                      style={!s.cobrado ? { background: '#2E7D32' } : {}}
                    >
                      {s.cobrado ? 'Marcar sin cobrar' : 'Cobrar'}
                    </Button>
                    <Space size={4}>
                      <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditar(s)} />
                      <Popconfirm title="¿Eliminar?" onConfirm={() => eliminar(s.id)} okText="Sí" cancelText="No">
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  </Space>
                </div>
              </Card>
            )
          })}
        </Space>
      )}
    </>
  )
}

// ==================== CLIENTES ====================
function VistaClientes() {
  const isMobile = useIsMobile()
  const [clientes, setClientes] = useState([])
  const [tipos, setTipos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [form] = Form.useForm()
  const [mostrarForm, setMostrarForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [preciosCliente, setPreciosCliente] = useState({ cliente: null, precios: [] })
  const [formPrecio] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setCargando(true)
    try {
      const [c, t] = await Promise.all([api('/clientes'), api('/tipos-trabajo')])
      setClientes(c); setTipos(t)
    } catch (err) { message.error('Error cargando') }
    setCargando(false)
  }

  const abrirNuevo = () => { setEditando(null); form.resetFields(); setMostrarForm(true) }
  const abrirEditar = (c) => { setEditando(c); form.setFieldsValue(c); setMostrarForm(true) }

  const guardar = async (values) => {
    try {
      if (editando) {
        await api(`/clientes/${editando.id}`, { method: 'PUT', body: JSON.stringify(values) })
      } else {
        await api('/clientes', { method: 'POST', body: JSON.stringify(values) })
      }
      message.success('Guardado')
      setMostrarForm(false)
      cargar()
    } catch (err) { message.error(err.message || 'Error') }
  }

  const eliminar = async (id) => {
    try {
      await api(`/clientes/${id}`, { method: 'DELETE' })
      message.success('Eliminado')
      cargar()
    } catch (err) { message.error(err.message || 'Error eliminando') }
  }

  const abrirPrecios = async (c) => {
    try {
      const precios = await api(`/clientes/${c.id}/precios`)
      setPreciosCliente({ cliente: c, precios })
      formPrecio.resetFields()
    } catch (err) { message.error('Error cargando precios') }
  }

  const guardarPrecio = async (v) => {
    try {
      await api(`/clientes/${preciosCliente.cliente.id}/precios`, { method: 'POST', body: JSON.stringify(v) })
      message.success('Precio guardado')
      const precios = await api(`/clientes/${preciosCliente.cliente.id}/precios`)
      setPreciosCliente({ ...preciosCliente, precios })
      formPrecio.resetFields()
    } catch (err) { message.error('Error') }
  }

  const eliminarPrecio = async (id) => {
    try {
      await api(`/precios-cliente/${id}`, { method: 'DELETE' })
      const precios = await api(`/clientes/${preciosCliente.cliente.id}/precios`)
      setPreciosCliente({ ...preciosCliente, precios })
    } catch (err) { message.error('Error') }
  }

  return (
    <>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32' }}>Nuevo cliente</Button>
      </Space>

      <Drawer
        title={editando ? 'Editar cliente' : 'Nuevo cliente'}
        open={mostrarForm}
        onClose={() => setMostrarForm(false)}
        width={isMobile ? '100%' : 400}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setMostrarForm(false)}>Cancelar</Button>
            <Button type="primary" onClick={() => form.submit()}>Guardar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Nombre" name="nombre" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item label="Teléfono" name="telefono"><Input /></Form.Item>
          <Form.Item label="Email" name="email"><Input /></Form.Item>
          <Form.Item label="NIF / CIF" name="nif"><Input /></Form.Item>
          <Form.Item label="Dirección" name="direccion"><Input /></Form.Item>
          <Form.Item label="Municipio" name="municipio"><Input /></Form.Item>
          <Form.Item label="Notas" name="notas"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Drawer>

      <Modal
        title={`Precios de ${preciosCliente.cliente?.nombre || ''}`}
        open={!!preciosCliente.cliente}
        onCancel={() => setPreciosCliente({ cliente: null, precios: [] })}
        footer={null}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>Define precios específicos (override del precio base).</Text>
        <List
          style={{ marginTop: 8 }}
          dataSource={preciosCliente.precios}
          locale={{ emptyText: 'Usa el precio base del catálogo' }}
          renderItem={p => (
            <List.Item
              actions={[<Button size="small" danger icon={<DeleteOutlined />} onClick={() => eliminarPrecio(p.id)} />]}
            >
              <div>
                <Text strong>{p.tipo_trabajo_nombre}</Text>
                <Tag color={tipoFactColor[p.tipo_facturacion]} style={{ marginLeft: 6, fontSize: 10 }}>{tipoFactLabel[p.tipo_facturacion]}</Tag>
                <div style={{ fontSize: 12 }}>
                  Precio: <Text strong style={{ color: '#1565C0' }}>{parseFloat(p.precio).toFixed(2)} €</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>(base: {parseFloat(p.precio_base).toFixed(2)} €)</Text>
                </div>
              </div>
            </List.Item>
          )}
        />
        <Divider>Añadir / actualizar precio</Divider>
        <Form form={formPrecio} layout="inline" onFinish={guardarPrecio}>
          <Form.Item name="tipo_trabajo_id" rules={[{ required: true }]} style={{ flex: 1 }}>
            <Select placeholder="Tipo de trabajo" style={{ minWidth: 180 }}>
              {tipos.map(t => <Select.Option key={t.id} value={t.id}>{t.nombre}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="precio" rules={[{ required: true }]}>
            <InputNumber min={0} step={1} placeholder="Precio" />
          </Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" icon={<PlusOutlined />} /></Form.Item>
        </Form>
      </Modal>

      {cargando ? (
        <div style={{ textAlign: 'center', marginTop: 60 }}><Spin size="large" /></div>
      ) : clientes.length === 0 ? (
        <Empty description="No hay clientes" style={{ marginTop: 40 }} />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {clientes.map(c => (
            <Card key={c.id} size="small" hoverable style={{ borderRadius: 12, borderLeft: '3px solid #00838F' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 14 }}>{c.nombre}</Text>
                  {c.activo === false && <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Inactivo</Tag>}
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                    {c.telefono && <span><PhoneOutlined /> {c.telefono} </span>}
                    {c.email && <span style={{ marginLeft: 8 }}><MailOutlined /> {c.email}</span>}
                  </div>
                  {c.municipio && <div style={{ fontSize: 11, color: '#666' }}><EnvironmentOutlined /> {c.municipio}</div>}
                  <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Tag color="blue" style={{ fontSize: 10 }}>{c.total_servicios || 0} servicios</Tag>
                    <Tag color="green" style={{ fontSize: 10 }}>Facturado: {parseFloat(c.facturado_total || 0).toFixed(0)} €</Tag>
                    {parseFloat(c.pendiente_cobro || 0) > 0 && (
                      <Tag color="orange" style={{ fontSize: 10 }}>Pendiente: {parseFloat(c.pendiente_cobro).toFixed(0)} €</Tag>
                    )}
                  </div>
                </div>
                <Space direction="vertical" size={2}>
                  <Button size="small" icon={<DollarOutlined />} onClick={() => abrirPrecios(c)}>Precios</Button>
                  <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditar(c)} />
                    <Popconfirm title="¿Eliminar?" onConfirm={() => eliminar(c.id)} okText="Sí" cancelText="No">
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                </Space>
              </div>
            </Card>
          ))}
        </Space>
      )}
    </>
  )
}

// ==================== TIPOS TRABAJO ====================
function VistaTipos() {
  const isMobile = useIsMobile()
  const [tipos, setTipos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setCargando(true)
    try { setTipos(await api('/tipos-trabajo')) }
    catch (err) { message.error('Error cargando') }
    setCargando(false)
  }

  const abrirNuevo = () => {
    setEditando(null); form.resetFields()
    form.setFieldsValue({ tipo_facturacion: 'hora', precio_base: 0 })
    setMostrarForm(true)
  }
  const abrirEditar = (t) => { setEditando(t); form.setFieldsValue(t); setMostrarForm(true) }

  const guardar = async (v) => {
    try {
      if (editando) await api(`/tipos-trabajo/${editando.id}`, { method: 'PUT', body: JSON.stringify(v) })
      else await api('/tipos-trabajo', { method: 'POST', body: JSON.stringify(v) })
      message.success('Guardado'); setMostrarForm(false); cargar()
    } catch (err) { message.error(err.message || 'Error') }
  }

  const eliminar = async (id) => {
    try { await api(`/tipos-trabajo/${id}`, { method: 'DELETE' }); message.success('Eliminado'); cargar() }
    catch (err) { message.error(err.message || 'Error') }
  }

  return (
    <>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Catálogo de trabajos con precio base</Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32' }}>Nuevo tipo</Button>
      </Space>

      <Drawer
        title={editando ? 'Editar tipo' : 'Nuevo tipo de trabajo'}
        open={mostrarForm}
        onClose={() => setMostrarForm(false)}
        width={isMobile ? '100%' : 380}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setMostrarForm(false)}>Cancelar</Button>
            <Button type="primary" onClick={() => form.submit()}>Guardar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Nombre" name="nombre" rules={[{ required: true }]}>
            <Input placeholder="Ej: Arar, Gradeo, Siembra..." />
          </Form.Item>
          <Form.Item label="Descripción" name="descripcion">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Tipo de facturación" name="tipo_facturacion" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="hora">Por hora</Select.Option>
              <Select.Option value="servicio">Por servicio (precio fijo)</Select.Option>
              <Select.Option value="tahulla">Por tahulla trabajada</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Precio base (€)" name="precio_base" rules={[{ required: true }]}>
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
          {editando && (
            <Form.Item name="activo" valuePropName="checked">
              <Checkbox>Activo</Checkbox>
            </Form.Item>
          )}
        </Form>
      </Drawer>

      {cargando ? (
        <div style={{ textAlign: 'center', marginTop: 60 }}><Spin /></div>
      ) : tipos.length === 0 ? (
        <Empty description="No hay tipos de trabajo" style={{ marginTop: 40 }} />
      ) : (
        <Row gutter={[8, 8]}>
          {tipos.map(t => (
            <Col xs={24} sm={12} key={t.id}>
              <Card size="small" style={{ borderRadius: 12, borderLeft: `3px solid ${t.activo === false ? '#999' : '#6A1B9A'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <Text strong>{t.nombre}</Text>
                    {t.activo === false && <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Inactivo</Tag>}
                    <div style={{ marginTop: 4 }}>
                      <Tag color={tipoFactColor[t.tipo_facturacion]} style={{ fontSize: 10 }}>{tipoFactLabel[t.tipo_facturacion]}</Tag>
                      <Text strong style={{ color: '#1565C0', marginLeft: 6 }}>
                        {parseFloat(t.precio_base).toFixed(2)} € / {tipoFactUnidad[t.tipo_facturacion]}
                      </Text>
                    </div>
                    {t.descripcion && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>{t.descripcion}</Text>}
                  </div>
                  <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditar(t)} />
                    <Popconfirm title="¿Eliminar?" onConfirm={() => eliminar(t.id)} okText="Sí" cancelText="No">
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </>
  )
}

// ==================== DASHBOARD ====================
function VistaDashboard() {
  const [data, setData] = useState(null)
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    api('/servicios/dashboard')
      .then(setData)
      .catch(() => message.error('Error cargando dashboard'))
      .finally(() => setCargando(false))
  }, [])

  if (cargando) return <div style={{ textAlign: 'center', marginTop: 60 }}><Spin size="large" /></div>
  if (!data) return null

  const k = data.kpis || {}
  const meses = { '01': 'Ene','02': 'Feb','03': 'Mar','04': 'Abr','05': 'May','06': 'Jun','07': 'Jul','08': 'Ago','09': 'Sep','10': 'Oct','11': 'Nov','12': 'Dic' }
  const maxMes = Math.max(...data.por_mes.map(m => parseFloat(m.total) || 0), 1)
  const maxCliente = Math.max(...data.top_clientes.map(c => parseFloat(c.total) || 0), 1)

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {/* KPIs */}
      <Row gutter={[8, 8]}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title={<Text style={{ fontSize: 11 }}>Facturado mes</Text>} value={parseFloat(k.facturado_mes || 0)} precision={2} suffix="€" valueStyle={{ fontSize: 18, color: '#2E7D32' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>{k.servicios_mes || 0} servicios</Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title={<Text style={{ fontSize: 11 }}>Pendiente cobro</Text>} value={parseFloat(k.pendiente_cobro || 0)} precision={2} suffix="€" valueStyle={{ fontSize: 18, color: '#E65100' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title={<Text style={{ fontSize: 11 }}>Cobrado total</Text>} value={parseFloat(k.cobrado_total || 0)} precision={2} suffix="€" valueStyle={{ fontSize: 18, color: '#1565C0' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title={<Text style={{ fontSize: 11 }}>Facturado total</Text>} value={parseFloat(k.facturado_total || 0)} precision={2} suffix="€" valueStyle={{ fontSize: 18, color: '#6A1B9A' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>{k.total_servicios || 0} servicios</Text>
          </Card>
        </Col>
      </Row>

      {/* Facturación por mes */}
      <Card size="small" title="Facturación últimos 12 meses" style={{ borderRadius: 12 }}>
        {data.por_mes.length === 0 ? (
          <Empty description="Sin datos" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {data.por_mes.map(m => {
              const total = parseFloat(m.total) || 0
              const pct = (total / maxMes) * 100
              const [y, mm] = m.mes.split('-')
              return (
                <div key={m.mes}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <Text>{meses[mm]} {y.slice(2)}</Text>
                    <Text strong>{total.toFixed(2)} € <Text type="secondary" style={{ fontSize: 11 }}>({m.cantidad})</Text></Text>
                  </div>
                  <div style={{ background: '#f0f0f0', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #2E7D32, #66BB6A)' }} />
                  </div>
                </div>
              )
            })}
          </Space>
        )}
      </Card>

      {/* Top clientes */}
      <Card size="small" title="Top 5 clientes por facturación" style={{ borderRadius: 12 }}>
        {data.top_clientes.length === 0 ? (
          <Empty description="Sin datos" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {data.top_clientes.map((c, i) => {
              const total = parseFloat(c.total) || 0
              const pct = (total / maxCliente) * 100
              return (
                <div key={c.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <Text><Tag color="blue" style={{ fontSize: 10 }}>{i + 1}</Tag> {c.nombre}</Text>
                    <Text strong>{total.toFixed(2)} € <Text type="secondary" style={{ fontSize: 11 }}>({c.servicios})</Text></Text>
                  </div>
                  <div style={{ background: '#f0f0f0', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #00838F, #4DB6AC)' }} />
                  </div>
                </div>
              )
            })}
          </Space>
        )}
      </Card>

      {/* Top tipos */}
      <Card size="small" title="Tipos de trabajo más facturados" style={{ borderRadius: 12 }}>
        {data.top_tipos.length === 0 ? (
          <Empty description="Sin datos" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={data.top_tipos}
            renderItem={t => (
              <List.Item>
                <div style={{ flex: 1 }}>
                  <Text strong>{t.nombre}</Text>
                  <Tag color={tipoFactColor[t.tipo_facturacion]} style={{ marginLeft: 6, fontSize: 10 }}>{tipoFactLabel[t.tipo_facturacion]}</Tag>
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{t.cantidad} servicios</Text>
                </div>
                <Text strong style={{ color: '#1565C0' }}>{parseFloat(t.total).toFixed(2)} €</Text>
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Pendientes de cobro */}
      <Card size="small" title={<span><WarningOutlined style={{ color: '#E65100' }} /> Pendientes de cobro</span>} style={{ borderRadius: 12 }}>
        {data.pendientes_cobro.length === 0 ? (
          <Empty description="Todo cobrado" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={data.pendientes_cobro}
            renderItem={p => {
              const dias = p.dias != null ? parseInt(p.dias) : 0
              const color = dias > 30 ? 'red' : dias > 15 ? 'orange' : 'gold'
              return (
                <List.Item>
                  <div style={{ flex: 1 }}>
                    <Text strong>{p.cliente_nombre}</Text>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{p.tipo_trabajo_nombre} · {dayjs(p.fecha).format('DD/MM/YY')}</Text>
                  </div>
                  <Space>
                    <Tag color={color} style={{ fontSize: 10 }}>{dias}d</Tag>
                    <Text strong style={{ color: '#E65100' }}>{parseFloat(p.importe_total).toFixed(2)} €</Text>
                  </Space>
                </List.Item>
              )
            }}
          />
        )}
      </Card>
    </Space>
  )
}
